import { useState } from "react";
import { Upload } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { EmptyState } from "#/components/common/EmptyState";
import { DataPagination } from "#/components/data-table/DataPagination";
import { useAuth } from "#/context/AuthProvider";
import { useDebouncedValue } from "#/features/inventory/hooks";
import { useAllDocuments, useUploadDocument } from "../hooks";
import { AdminDocumentFiltersBar, CURRENT_YEAR, hasCustomFilters } from "../components/DocumentFilters";
import { DocumentViewer } from "../components/DocumentViewer";
import { DocumentUploadForm } from "../components/DocumentUploadForm";
import type { AdminDocumentFilters } from "../types";

export default function DocumentsPage() {
  const [page, setPage] = useState(0);
  // Defaults to the current year — tick "All Years" in the filter bar to see everything.
  const [filters, setFilters] = useState<AdminDocumentFilters>({ year: CURRENT_YEAR });
  const [showUpload, setShowUpload] = useState(false);

  const { hasRole, hasAnyRole, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);
  // Access log (who viewed/downloaded) and restoring a soft-deleted document are both
  // scoped to HR_DOCUMENT_MANAGE / SUPER_ADMIN — same tier that can see CONFIDENTIAL
  // documents. Restore has no time limit.
  const canManageDocuments = isAdmin || hasPermission("HR_DOCUMENT_MANAGE");

  // The free-text search is debounced so the query (and the page-flip it causes) only
  // fires once typing pauses — every other filter still applies immediately.
  const debouncedSearch = useDebouncedValue(filters.search, 400);
  const effectiveFilters: AdminDocumentFilters = { ...filters, search: debouncedSearch };

  // useAllDocuments → GET /api/hr/documents/all (HR_DOCUMENT_VIEW | HR_DOCUMENT_MANAGE | SUPER_ADMIN | HR_DOCUMENT_VIEW_DEPARTMENT)
  const query = useAllDocuments(page, 20, effectiveFilters);
  const upload = useUploadDocument();

  function handleFilterChange(f: AdminDocumentFilters) {
    setFilters(f);
    setPage(0);
  }

  if (query.isLoading) return <PageLoader label="Loading documents…" />;

  if (query.isError) {
    return (
      <ErrorState
        title="Failed to load documents"
        message={
          query.error instanceof Error ? query.error.message : "Could not load documents."
        }
      />
    );
  }

  const data = query.data!;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Documents</h1>
        </div>
        <button
          type="button"
          onClick={() => setShowUpload((v) => !v)}
          className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          <Upload className="h-4 w-4" />
          {showUpload ? "Cancel" : "Upload Document"}
        </button>
      </div>

      {/* Upload panel */}
      {showUpload && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5">
          <h2 className="mb-3 font-bold text-slate-800">Upload a document</h2>
          <DocumentUploadForm
            onUploaded={() => {
              setShowUpload(false);
              void query.refetch();
            }}
          />
        </div>
      )}

      {/* Filters — search + type + module */}
      <AdminDocumentFiltersBar filters={filters} onChange={handleFilterChange} />

      {/* Results */}
      {data.content.length === 0 ? (
        <EmptyState
          title="No documents found"
          description={
            hasCustomFilters(filters)
              ? "No documents match your filters. Try broadening your search."
              : `No documents uploaded in ${CURRENT_YEAR} yet — tick "All Years" to see older ones, or upload one above.`
          }
        />
      ) : (
        <>
          <DocumentViewer
            documents={data.content}
            canViewAccessLog={canManageDocuments}
            canRestore={canManageDocuments}
          />
          <DataPagination
            page={data.number}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            size={data.size}
            onPageChange={setPage}
            onSizeChange={() => setPage(0)}
          />
        </>
      )}
    </div>
  );
}
